import { yupResolver } from "@hookform/resolvers/yup";
import { Translate } from "@omni/types/utils/translate";
import { Button } from "@omni/ui/components/Button/button.component";
import { Card } from "@omni/ui/components/Card/card";
import { AxiosError } from "axios";
import { useEffect } from "react";

import { BonusCardBalanceResponse } from "@/api/hooks/customerService.types";
import { NumericTextField } from "@/components/Form/RHF/FormFields/NumericTextField/NumericTextfield.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";

import { useOmniForm } from "../Form/RHF/Utils/useOmniForm";
import {
    BonusCardRequestBalanceFormValidationSchema,
    BonusCardRequestBalanceFormValues
} from "./bonusCardRequestBalance.schema";

const FORM_ID = "bonusCardBalanceRequestForm";

type BonusCardRequestBalanceFormProps = {
    onRequestBalanceFor: (bonusCardNumber: string) => Promise<void>;
    error?: AxiosError<string, BonusCardBalanceResponse> | null;
    loading: boolean;
    bonusCardnumber: string;
};

const BonusCardRequestBalanceForm = ({
    onRequestBalanceFor,
    error,
    bonusCardnumber,
    loading
}: BonusCardRequestBalanceFormProps) => {
    const { t } = useGranite();

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            bonusCardNumber: ""
        },
        resolver: yupResolver(BonusCardRequestBalanceFormValidationSchema)
    });

    const { subscribe } = methods;

    // biome-ignore lint/correctness/useExhaustiveDependencies: TODO
    useEffect(() => {
        if (!error?.response) return;

        let message = "";

        if (error?.response?.status === 429) {
            message = "form.validation.bonusCard.tooManyRequests";
        }

        if (!message && error?.response?.data) {
            message = `general.form.input.validation.bonusCard.${error.response.data}`;
        }

        methods.setError("bonusCardNumber", {
            type: "validate",
            message: () => (t: Translate) =>
                t(message) || t("form.validation.bonusCard.invalid")
        });
    }, [error]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: TODO
    useEffect(() => {
        const callback = subscribe({
            formState: { values: true },
            callback: ({ values }) => {
                if (
                    values?.bonusCardNumber !== bonusCardnumber &&
                    bonusCardnumber !== ""
                ) {
                    onRequestBalanceFor("");
                }
            }
        });
        return () => callback();
    }, [subscribe]);

    const onFormSubmit = async ({
        bonusCardNumber
    }: BonusCardRequestBalanceFormValues) => {
        await onRequestBalanceFor(bonusCardNumber);
    };

    return (
        <div>
            <Card>
                <Card.Body>
                    <FormInfoProvider
                        formId={FORM_ID}
                        withNeverbounce={false}
                    >
                        {() => (
                            <OmniFormContextProvider {...methods}>
                                <form
                                    id={FORM_ID}
                                    onSubmit={methods.handleSubmit(
                                        onFormSubmit
                                    )}
                                >
                                    <Row>
                                        <Col>
                                            <NumericTextField<BonusCardRequestBalanceFormValues>
                                                name="bonusCardNumber"
                                                label={t(
                                                    "general.form.bonusCardRequestBalance.label"
                                                )}
                                                placeholder={t(
                                                    "general.form.bonusCardRequestBalance.placeholder"
                                                )}
                                                microCopy={t(
                                                    "general.form.bonusCardRequestBalance.microcopy"
                                                )}
                                                hideLabel={true}
                                                required={true}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <Button
                                                test="bonusCardRequestBalance-button-submit"
                                                type="submit"
                                                size="md"
                                                loading={loading}
                                            >
                                                {t(
                                                    "general.form.bonusCardRequestBalance.submit"
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>
                                </form>
                            </OmniFormContextProvider>
                        )}
                    </FormInfoProvider>
                </Card.Body>
            </Card>
        </div>
    );
};

export { BonusCardRequestBalanceForm };
